import {AuthContext} from "../contexts/AuthContext.tsx";
import {ReactNode, useCallback, useEffect, useRef, useState} from "react";
import {IUser} from "../interfaces/IUser.ts";
import apiNet from "../newApi/apiNet.ts";
import {LocalStorageService} from "../services/localStorageService.ts";
import {StorageKeys} from "../consts/storageKeys.ts";
import axios, {AxiosError, InternalAxiosRequestConfig} from "axios";
import axiosClientNet from "../newApi/axiosClientNet.ts";
import {InternalServerErrorException} from "../exceptions/internalServerErrorException.ts";
import {BadRequestException} from "../exceptions/badRequestException.ts";
import {AxiosException} from "../exceptions/axiosException.ts";
import {IAxiosErrorResponse} from "../interfaces/IAxiosErrorResponse.ts";
import {SessionStorageService} from "../services/sessionStorageService.ts";
import {useNavigate} from "react-router-dom";
import {useNotification} from "./NotificationProvider.tsx";
import {NotificationStatus} from "../components/notification/Notification.tsx";
import {IAuthenticatedResponse} from "../response/authenticatedResponse.ts";
import {IReminder} from "../interfaces/IReminder.ts";

interface Props {
    children?: ReactNode;
}

interface IAxiosRequestConfig extends InternalAxiosRequestConfig {
    _retry?: boolean;
}

const AuthProvider = ({children}: Props) => {
    const _localStorageService = new LocalStorageService();
    const _sessionStorageService = new SessionStorageService();
    const _navigate = useNavigate();
    const _notificationService = useNotification();
    const [user, setUser] = useState<IUser | null | undefined>(undefined);
    // For PHP backend
    // const [userPHP, setUserPHP] = useState<IUser | null | undefined>(undefined);
    // const [isUserAuthPHP, setIsUserAuthPHP] = useState(() => _localStorageService.getItem<boolean>(StorageKeys.REFRESH_TOKEN));
    const [token, setToken] = useState("");
    const [rememberMe, setRememberMe] = useState<boolean>(() => {
        return !!_localStorageService.getItem<boolean>(StorageKeys.REFRESH_TOKEN);
    });
    const [isRefreshTokenAvailable, setIsRefreshTokenAvailable] = useState(() => {
        // let isRefreshTokenAvailable = _localStorageService.getItem<boolean>(StorageKeys.REFRESH_TOKEN);
        // if (isRefreshTokenAvailable) {
        //     setRememberMe(true);
        //     return isRefreshTokenAvailable;
        // }
        // isRefreshTokenAvailable = _sessionStorageService.getItem<boolean>(StorageKeys.REFRESH_TOKEN);
        // if (isRefreshTokenAvailable) {
        //     setRememberMe(false);
        //     return isRefreshTokenAvailable;
        // }
        // return false;

        const fromLocal = _localStorageService.getItem<boolean>(StorageKeys.REFRESH_TOKEN);
        const fromSession = _sessionStorageService.getItem<boolean>(StorageKeys.REFRESH_TOKEN);
        return !!(fromLocal || fromSession);
    });

    const isRefreshing = useRef(false);
    const failedQueue = useRef<Array<{ resolve: (value?: unknown) => void, reject: (reason?: any) => void }>>([]);

    const getUserFromAuthenticatedResponse = (response: IAuthenticatedResponse): IUser => {
        const user = response.user;
        const reminders: IReminder[] = [];
        if (user.reminders)
            user.reminders.forEach(reminder =>
                reminders.push({
                    id: reminder.id,
                    title: reminder.title,
                    remindAt: new Date(reminder.remindAt),
                    channelId: reminder.channelId,
                    isDisplayed: false
                })
            );
        return {
            id: user.id,
            profiles: user.profiles,
            recordingIds: user.recordingIds,
            reminders: reminders,
            roles: user.roles,
            userName: user.userName
        };
    }

    const loginPHP = async (reCaptchaToken: string, id: number, password: string, rememberMe: boolean, osInfo: string, fcmToken: string) => {
        try {
            const response = await apiNet.loginPHP(reCaptchaToken, id, password, rememberMe, osInfo, fcmToken);
            _localStorageService.setItem<boolean>(StorageKeys.REFRESH_TOKEN, true);
            // TODO: Remove this
            // userPHP;
            // isUserAuthPHP;
            // setIsUserAuthPHP(true);
            //
            setUser(response.data.user);
            setToken("true");
            setIsRefreshTokenAvailable(true);
            setRememberMe(rememberMe);
        } catch (e) {
            const error = e as AxiosError<IAxiosErrorResponse>;
            if (axios.isAxiosError(error) && error.response) {
                throw new AxiosException(error.response.status, error.response.data.MsgCode, error.response.data.Message);
            } else
                throw new Error("Something went wrong...");
        }
    }


    const login = async (reCaptchaToken: string, email: string, password: string, rememberMe: boolean, osInfo: string) => {
        try {
            const response = await apiNet.login(reCaptchaToken, email, password, rememberMe, osInfo);

            if (response.status === 200) {
                if (rememberMe)
                    _localStorageService.setItem<boolean>(StorageKeys.REFRESH_TOKEN, true);
                else
                    _sessionStorageService.setItem<boolean>(StorageKeys.REFRESH_TOKEN, true);

                setToken(response.data.accessToken);
                setUser(getUserFromAuthenticatedResponse(response.data));
                setIsRefreshTokenAvailable(true);
                setRememberMe(rememberMe);
            }
        } catch (e) {
            const error = e as AxiosError<IAxiosErrorResponse>;
            if (axios.isAxiosError(error) && error.response) {
                throw new AxiosException(error.response.status, error.response.data.MsgCode, error.response.data.Message);
            } else
                throw new Error("Something went wrong...");
        }
    }
    const logout = async () => {
        try {
            await apiNet.logout();
            _logoutFromApp();
            _notificationService.showNotification("Sukces", "Wylogowanie przebiegło pomyślnie", NotificationStatus.success)
            _navigate("/");
        } catch (e) {
            throw new Error("Something went wrong...");
        }
    }

    const logoutFromAllDevices = async () => {
        try {
            await apiNet.logoutFromAllDevices();
            _logoutFromApp();
            _notificationService.showNotification("Sukces", "Zostałeś wylogowany ze wszystkich urządzeń.", NotificationStatus.success)
            _navigate("/");
        } catch (e) {
            const error = e as Error | AxiosError;
            if (axios.isAxiosError(error) && error.response) {
                if (error.response.status === 404) {
                    await logout();
                    _notificationService.showNotification("Niepowodzenie", "Żądanie nie mogło zostać obsłużone. Proszę się zalogować ponownie i powtórzyć operację.", NotificationStatus.failure);
                }
                throw new AxiosException(error.response.status, error.response.data.MsgCode, error.response.data.Messege)
            }
            _notificationService.showNotification("Niepowodzenie", "Wylogowanie nie powiodło się. Wystąpił błąd po stronie serwera. Spróbuj ponownie za kilka minut.", NotificationStatus.failure)
            throw InternalServerErrorException("Something went wrong...");
        }
    }
    // const logoutFromAllDevices = async () => {
    //     try {
    //         await apiNet.logoutFromAllDevices();
    //         _logoutFromApp();
    //         _notificationService.showNotification("Sukces", "Wylogowanie przebiegło pomyślnie", NotificationStatus.success);
    //         _navigate("/");
    //     } catch (e) {
    //         throw new Error("Something went wrong...");
    //     }
    // }

    const _logoutFromApp = () => {
        setUser(null);
        setToken("");
        setIsRefreshTokenAvailable(false);
        setRememberMe(false);
        _localStorageService.removeItem(StorageKeys.REFRESH_TOKEN);
        _sessionStorageService.removeItem(StorageKeys.REFRESH_TOKEN);
    }

    const confirmRegistration = async (token: string) => {
        try {
            await apiNet.confirmRegistration(token);
        } catch (e) {
            const error = e as Error | AxiosError;
            if (axios.isAxiosError(error) && error.response)
                new AxiosException(error.response.status, error.response.data.MsgCode, error.response.data.Messege)
            else
                throw InternalServerErrorException("Something went wrong...");
        }
    }

    const resetPassword = async (verificationToken: string, password: string, confirmPassword: string) => {
        try {
            await apiNet.resetPassword(verificationToken, password, confirmPassword);
        } catch (e) {
            const error = e as Error | AxiosError;
            if (axios.isAxiosError(error) && error.response && error.response.status === 400)
                throw BadRequestException(error.response.data.MsgCode, error.response.data.Messege);
            else
                throw InternalServerErrorException("Something went wrong...");
        }
    }


    const refreshToken = useCallback(async (rememberMe: boolean): Promise<string> => {
        try {
            // Używamy "gołego" axiosa, żeby nie wpaść w pętlę interceptorów axiosClientNet
            const response = await axios.post(
                `${import.meta.env.VITE_MIX_API_URL}/auth/refresh-token`,
                {rememberMe},
                {withCredentials: true}
            );

            const newAccessToken = response.data.accessToken;

            axiosClientNet.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;

            setToken(newAccessToken);
            setUser(getUserFromAuthenticatedResponse(response.data));
            setIsRefreshTokenAvailable(true);

            const storage = rememberMe ? _localStorageService : _sessionStorageService;
            storage.setItem(StorageKeys.REFRESH_TOKEN, true);

            return newAccessToken;
        } catch (e) {
            setToken("");
            setUser(null);
            setIsRefreshTokenAvailable(false);
            _localStorageService.removeItem(StorageKeys.REFRESH_TOKEN);
            _sessionStorageService.removeItem(StorageKeys.REFRESH_TOKEN);
            throw new Error("Refresh token is invalid or expired");
        }
    }, [setUser, setToken, setIsRefreshTokenAvailable]);


    // useLayoutEffect(() => {
    //     let authInterceptor: number;
    //     if (token && import.meta.env.VITE_BACKEND_TYPE === "dotnet") {
    //         authInterceptor = axiosClientNet.interceptors.request.use((config: IAxiosRequestConfig) => {
    //             config.headers.Authorization = !config._retry && token ? `Bearer ${token}` : config.headers.Authorization;
    //             return config;
    //         })
    //     }
    //     return () => axiosClientNet.interceptors.request.eject(authInterceptor);
    // }, [token]);

    useEffect(() => {
        let authInterceptor: number | null = null;

        if (token && import.meta.env.VITE_BACKEND_TYPE === "dotnet") {
            authInterceptor = axiosClientNet.interceptors.request.use((config: IAxiosRequestConfig) => {
                config.headers.Authorization = !config._retry && token ? `Bearer ${token}` : config.headers.Authorization;
                return config;
            });
        }

        return () => {
            if (authInterceptor !== null) {
                axiosClientNet.interceptors.request.eject(authInterceptor);
            }
        };
    }, [token]);

    const processQueue = (error: any, token: string | null = null) => {
        failedQueue.current.forEach(prom => {
            if (error) {
                prom.reject(error);
            } else {
                prom.resolve(token);
            }
        });
        failedQueue.current = []; // Czyszczenie kolejki
    };

    useEffect(() => {
        if (import.meta.env.VITE_BACKEND_TYPE === 'php') {
            if (!token && isRefreshTokenAvailable) {
                setToken("PHPTOKEN");
            }
        }
        if (import.meta.env.VITE_BACKEND_TYPE === 'dotnet') {
            (async () => {
                if (!token && isRefreshTokenAvailable) {
                    await refreshToken(rememberMe);
                }
            })();
        }
    }, [token, isRefreshTokenAvailable, rememberMe]);

    // useLayoutEffect(() => {
    //     // let refreshInterceptor: number;
    //     const refreshInterceptor = axiosClientNet.interceptors.response.use((response) => response,
    //         async (error) => {
    //             console.log(error.response);
    //             const originalRequest = error.config;
    //             if (originalRequest && error.response?.status === 401 && !originalRequest._retry && isRefreshTokenAvailable) {
    //                 const accessToken = await refreshToken(rememberMe);
    //                 setToken(accessToken);
    //                 originalRequest.headers.Authorization = `Bearer ${accessToken}`;
    //                 originalRequest._retry = true; // Mark the request as retried to avoid infinite loops.
    //                 return axios(originalRequest);
    //             }
    //             return Promise.reject(error); // For all other errors, return the error as is.
    //         })
    //     return () => axiosClientNet.interceptors.response.eject(refreshInterceptor);
    // }, [token, isRefreshTokenAvailable, rememberMe]);

    useEffect(() => {
        const refreshInterceptor = axiosClientNet.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;
                if (originalRequest && error.response?.status === 401 && !originalRequest._retry && isRefreshTokenAvailable) {
                    if (isRefreshing.current) {
                        return new Promise((resolve, reject) => {
                            failedQueue.current.push({resolve, reject});
                        })
                            .then((token) => {
                                console.log(token)
                                originalRequest.headers.Authorization = `Bearer ${token}`;
                                return axiosClientNet(originalRequest);
                            })
                            .catch((err) => {
                                return Promise.reject(err);
                            });
                    }
                    originalRequest._retry = true;
                    isRefreshing.current = true;
                    try {
                        const accessToken = await refreshToken(rememberMe);
                        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
                        processQueue(null, accessToken);

                        return axiosClientNet(originalRequest);
                    } catch (refreshError) {
                        processQueue(refreshError, null);
                        return Promise.reject(refreshError);
                    } finally {
                        isRefreshing.current = false;
                    }
                }
                return Promise.reject(error);
            }
        );

        return () => axiosClientNet.interceptors.response.eject(refreshInterceptor);
    }, [isRefreshTokenAvailable, rememberMe]);


    return <AuthContext.Provider
        value={{
            user,
            token,
            isRefreshTokenAvailable: isRefreshTokenAvailable ?? false,
            setUser,
            login,
            loginPHP,
            logout,
            logoutFromAllDevices,
            confirmRegistration,
            resetPassword
        }}>
        {children}
    </AuthContext.Provider>
}
export default AuthProvider;